/** Partner → My Billing: what MSC charges, the card or bank account on file, payment history, pay now. */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, Landmark } from 'lucide-react';
import api from '../lib/api';
import { Card, PageHeader, btnGhost, btnPrimary, errorText, money } from '../components/finance/financeUi';
import { CardForm } from '../components/billing/CardForm';
import { BankForm } from '../components/billing/BankForm';
import { ChargesTable } from '../components/billing/ChargesTable';

const TestModeBanner = ({ mode }) => (mode === 'simulated'
  ? <p className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid="billing-test-mode">Test mode — no real money moves yet.</p>
  : null);

function MethodCard({ method, onSaved }) {
  const [tab, setTab] = useState(method ? null : 'card');
  const saved = () => { setTab(null); onSaved(); };
  return (
    <Card title="Payment method" testid="billing-method-card">
      <div className="space-y-4 px-5 py-4">
        {method && <div className="flex items-center gap-3" data-testid="billing-method-current">
          {method.type === 'card' ? <CreditCard className="h-5 w-5" /> : <Landmark className="h-5 w-5" />}
          <span className="font-medium">{method.brand} ending {method.last4}</span>
          {method.holder_name && <span className="text-sm text-muted-foreground">{method.holder_name}</span>}
        </div>}
        <div className="flex gap-2">
          <button type="button" className={tab === 'card' ? btnPrimary : btnGhost} onClick={() => setTab('card')} data-testid="billing-tab-card"><CreditCard className="h-4 w-4" />{method ? 'Use a card' : 'Card'}</button>
          <button type="button" className={tab === 'bank' ? btnPrimary : btnGhost} onClick={() => setTab('bank')} data-testid="billing-tab-bank"><Landmark className="h-4 w-4" />{method ? 'Use a bank account' : 'Bank account'}</button>
        </div>
        {tab === 'card' && <CardForm onSaved={saved} />}
        {tab === 'bank' && <BankForm onSaved={saved} />}
      </div>
    </Card>
  );
}

export default function BillingPage() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.request('/me/billing').then(setD).catch((e) => toast.error(errorText(e))), []);
  useEffect(() => { load(); }, [load]);
  const afterSave = async () => {
    const r = await api.request('/me/billing').catch(() => null);
    if (!r) return;
    setD(r);
    if (r.charges.some((c) => c.status === 'failed')) toast.error(`Payment method saved, but ${money(r.balance_due)} could not be collected — see Payment history`);
    else toast.success('Payment method saved');
  };
  if (!d) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  const pay = async () => {
    setBusy(true);
    try { const r = await api.request('/me/billing/pay', { method: 'POST' }); setD(r); toast[r.balance_due ? 'error' : 'success'](r.balance_due ? 'Some charges could not be collected' : 'Payment complete'); } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="billing-page">
      <PageHeader eyebrow="Billing" title="My Billing" subtitle="Your setup fee, equipment and monthly software subscription with Merchant Solutions Corp." />
      <TestModeBanner mode={d.mode} />
      {d.commercial && <p className={`rounded-lg border px-4 py-2 text-sm ${d.commercial.commercial_access_requirement_satisfied ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200' : 'bg-muted/40'}`} data-testid="billing-commercial-status">
        {d.commercial.billing_responsibility !== 'USER_PAYS' ? `No payment needed — ${d.commercial.label.replace('Billing — ', '')}.`
          : d.commercial.commercial_access_requirement_satisfied ? 'Billing step complete.' : `To finish activation: ${d.commercial.missing.map((m) => ({ setup_fee: 'pay the setup fee', first_month: 'pay the first month', payment_method: 'add a card or bank account' }[m])).join(' and ')}.`}
      </p>}
      <div className="grid gap-4 md:grid-cols-3">
        <Card testid="billing-balance"><div className="px-5 py-4"><p className="text-xs uppercase tracking-wide text-muted-foreground">Balance due</p><p className="mt-1 text-3xl font-semibold tabular-nums" data-testid="billing-balance-amount">{money(d.balance_due)}</p>
          {d.balance_due > 0 && d.method && <button type="button" className={`${btnPrimary} mt-3`} disabled={busy} onClick={pay} data-testid="billing-pay-btn">Pay {money(d.balance_due)} now</button>}</div></Card>
        <Card testid="billing-subscription"><div className="px-5 py-4"><p className="text-xs uppercase tracking-wide text-muted-foreground">Monthly subscription</p><p className="mt-1 text-3xl font-semibold tabular-nums">{d.plan?.monthly_fee ? money(d.plan.monthly_fee) : '—'}</p>
          <p className="text-xs text-muted-foreground" data-testid="billing-next-date">{d.plan?.monthly_fee && d.plan?.status === 'active' ? `Next charge on ${d.plan.next_bill_on}` : d.plan?.status === 'paused' ? 'Paused' : 'No subscription set up yet'}</p></div></Card>
        <Card testid="billing-setup"><div className="px-5 py-4"><p className="text-xs uppercase tracking-wide text-muted-foreground">Setup fee</p><p className="mt-1 text-3xl font-semibold tabular-nums">{d.plan?.setup_fee ? money(d.plan.setup_fee) : '—'}</p></div></Card>
      </div>
      <MethodCard method={d.method} onSaved={afterSave} />
      <Card title="Payment history" testid="billing-history"><ChargesTable charges={d.charges} testid="billing-charges" /></Card>
    </div>
  );
}
